/**
 * MeslekiEgitimKarti.jsx — denetmen kişi sayfasının (PersonelDetayPage) Meslek sekmesindeki "Mesleki Eğitim"
 * kartı. Backlog #284 ilk maddesi (Deniz 2026-10-06: "Mesleki eğitim yükleme kısmı Meslek tab'ında çıksın. Çoğunlukla
 * istenmeyen bir belge zaten.").
 *
 * Belgeler sekmesindeki kutuda durum + İncele kalır, ama YÜKLEME artık burada. Yükleme ve inceleme mantığı sayfada
 * (`uploadKisiBelgesi` → BelgeYukleModal, inceleme penceresi); kart yalnızca durumu gösterir ve iki düğmeyi çağırır.
 *
 * #372 (backend #371): kişi bir fuarda BİRDEN ÇOK meslek taşıyabilir ve her mesleğin kendi sertifikası vardır — bu kart
 * mesleğin yuvası başına bir kez basılır (`MeslekiEgitimKartlari`). Tek meslekte başlık eskisi gibi kalır.
 *
 * @param doc       `documents.meslekiEgitim` (ya da mesleğin yuvasındaki `belge`)
 * @param istenen   mesleğin kuralı bu belgeyi istiyor mu (`gerekliBelgeler` / yuvanın `istenen`i)
 * @param meslekAdi birden çok meslekte kartın hangi mesleğe ait olduğu (başlığa eklenir); tek meslekte verilmez
 * @param sayilir   yuvanın `sayilir`i — onaylı görünen ama sayılmayan (süresi dolmuş, fuar için eski) sertifikayı söyler
 * @param id        ilk kart `meslek-egitim-karti` (Kişi Listesi'nden gelinen vurgu bunu arar), ötekiler kendi kimliğiyle
 * @param vurgu     Kişi Listesi'nden gri kareyle gelinince kart vurgulanır (#273)
 * @param onIncele  inceleme penceresi (dosya varsa)
 * @param onYukle   Yükle / Yeniden Yükle
 */
import React from "react";
import { Badge, Button, Card, CardBody, CardHeader } from "reactstrap";
import { GraduationCap } from "lucide-react";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import { FILE_APPROVAL_STATUS } from "../../services/projectService";
import { useTranslation } from "react-i18next";

export default function MeslekiEgitimKarti({
  doc, istenen, meslekAdi = null, sayilir = true, id = "meslek-egitim-karti", vurgu = false, onIncele, onYukle,
}) {
  const { t } = useTranslation("components");
  const hasFile = doc?.fileId != null;
  const info = doc?.status != null ? FILE_APPROVAL_STATUS[doc.status] : null;
  // İstenen bir meslekte onaylı görünen ama sayılmayan sertifika yeşil "tamam" gibi okunmasın: sarı.
  const sayilmiyor = istenen && sayilir === false && doc?.status === 1;
  const background = sayilmiyor ? STATUS_HEX.warning : (info ? STATUS_HEX[info.color] ?? NEUTRAL_GRAY : NEUTRAL_GRAY);
  return (
    <Card id={id} className={vurgu ? "border border-2 border-primary shadow" : ""} style={!istenen ? { opacity: 0.6 } : undefined}>
      <CardHeader className="bg-transparent d-flex align-items-center gap-2 flex-wrap">
        <GraduationCap size={16} className="text-muted" />
        <span className="fw-semibold">{t("meslekiEgitimKarti.meslekiEgitim")}{meslekAdi ? ` — ${meslekAdi}` : ""}</span>
        <Badge color="light" className="text-body border fw-normal">{istenen ? t("meslekiEgitimKarti.buMeslekIcinIstenir") : t("meslekiEgitimKarti.buMeslekIcinIstenmiyor")}</Badge>
      </CardHeader>
      <CardBody>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
            style={{ width: 44, height: 44, background }}
          >
            <GraduationCap size={20} color="#fff" />
          </div>
          <div>
            <Badge style={{ background }}>{info ? info.label : t("meslekiEgitimKarti.kayitYok")}</Badge>
            {doc?.expired && <span className="small text-warning ms-2">{t("meslekiEgitimKarti.suresiDolmus")}</span>}
            {sayilmiyor && !doc?.expired && <span className="small text-warning ms-2">{t("meslekiEgitimKarti.sayilmiyor")}</span>}
            {hasFile && (
              <div className="small text-muted mt-1">
                {doc.fileName ?? t("meslekiEgitimKarti.dosya")}{doc.expiryDate ? t("meslekiEgitimKarti.sonKullanma", { expiryDate: doc.expiryDate }) : ""}
              </div>
            )}
          </div>
          <div className="ms-auto d-flex gap-1 flex-wrap">
            {hasFile && <Button color="light" size="sm" onClick={onIncele}>{t("meslekiEgitimKarti.incele")}</Button>}
            {onYukle && (
              <Button color="primary" outline size="sm" onClick={onYukle}>{hasFile ? t("meslekiEgitimKarti.yenidenYukle") : t("common:yukle")}</Button>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
